import {
  WSEmptyState,
  WSLoader,
  WSMarginProps,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableRowMenuAction
} from "@wingspanhq/fe-component-library";
import {
  IEngagement,
  IRequirementDefinitionResponse
} from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { useAddRequirementToEngagementModal } from "../../../Payees/components/AddRequirementToEngagementModal";
import { useRemoveRequirementFromEngagementModal } from "../../../Payees/components/RemoveRequirementFromEngagementModal";
import { useRequirementDefinitionListAllQuery } from "../../../../query/requirementDefinitions/query/useRequirementDefinitionListAllQuery";
import { TypeCell } from "../../../RequirementDefinitions/components/RequirementsTable";
import {
  buildRequirementDetailsPath,
  useGoToRequirementDefinitionDetails
} from "../../../RequirementDefinitions/paths/helpers";

export interface RequirementsTableProps extends WSMarginProps {
  engagement: IEngagement;
}

export const RequirementsTable: React.FC<RequirementsTableProps> = ({
  engagement,
  ...elementProps
}) => {
  const history = useHistory();
  const requirementsDefinitionsListQuery =
    useRequirementDefinitionListAllQuery();
  const addRequirementToEngagementModal = useAddRequirementToEngagementModal();
  const removeRequirementFromEngagementModal =
    useRemoveRequirementFromEngagementModal();

  const goToRequirementDetails = useGoToRequirementDefinitionDetails();

  const tableData = useMemo(() => {
    const requirementDefinitions = requirementsDefinitionsListQuery.data || [];

    const list = requirementDefinitions.filter(requirement =>
      engagement.requirementDefinitionIds?.includes(
        requirement.requirementDefinitionId
      )
    );
    return list.map(requirement => ({
      id: requirement.requirementDefinitionId,
      data: requirement
    }));
  }, [requirementsDefinitionsListQuery.data, engagement]);

  const columns: Array<WSTableColumn<IRequirementDefinitionResponse>> = [
    {
      config: {
        header: {
          text: "Name"
        }
      },
      renderFunction: rowItem => (
        <WSTableCell
          text={rowItem.data.name}
          secondaryText={rowItem.data.description}
        />
      )
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem => (
        <TypeCell
          requirementType={rowItem.data.requirementType}
          requirementDataSourceSettingsId={rowItem.data.dataSourceId}
        />
      )
    }
  ];

  if (!requirementsDefinitionsListQuery.isFetched) {
    return <WSLoader.Spinner my="2XL" size="S" color="gray500" />;
  }

  return tableData.length > 0 ? (
    <WSTable<IRequirementDefinitionResponse>
      {...elementProps}
      loading={requirementsDefinitionsListQuery.isInitialLoading}
      tableData={tableData}
      columns={columns}
      rowMenuActions={(item): WSTableRowMenuAction[] => {
        return [
          {
            label: "View requirement",
            icon: "view",
            onClick: () => {
              goToRequirementDetails(item.id);
            }
          },
          {
            label: "Remove from engagement type",
            destructive: true,
            icon: "trash",
            onAsyncClick: async () => {
              await removeRequirementFromEngagementModal.open({
                engagement: engagement,
                requirementDefinition: item.data
              });
            }
          }
        ];
      }}
      onRowClick={item => {
        goToRequirementDetails(item.id);
      }}
      onRowControlClick={item => {
        openInNewTab(buildRequirementDetailsPath(item.id));
      }}
      onRowMousewheelClick={item => {
        openInNewTab(buildRequirementDetailsPath(item.id));
      }}
      data-testid="engagementRequirementsTable"
    />
  ) : (
    <WSEmptyState
      type="documents"
      title="No requirements yet"
      description="Once a requirement is added to this engagement type, it will appear here."
      actions={{
        buttons: [
          {
            label: "Add requirement",
            kind: "Secondary",
            onClick: () => {
              addRequirementToEngagementModal.open({
                requirementDefinitionIds: engagement.requirementDefinitionIds,
                engagementId: engagement.engagementId
              });
              // history.push({
              //   pathname: `${ENGAGEMENT_REQUIREMENTS_SETTINGS_ROOT_PATH}/create`,
              //   state: {
              //     engagementId
              //   }
              // });
            }
          }
          // {
          //   label: "Turn off requirements",
          //   kind: "Tertiary",
          //   onClick: () => {
          //     alert("TODO");
          //   }
          // }
        ]
      }}
      hasBorder={true}
      bgColor="gray"
      orientation="horizontal"
    />
  );
};
